import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import RecordDialog from "@/components/RecordDialog";
import { toast } from "sonner";
import { Plus, X, Cloud, Mail, ShieldCheck, Lock, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ROLE_OPTS = { static: [
  { value: "super_admin", label: "Super Administrator" },
  { value: "admin", label: "Administrator" },
  { value: "producer", label: "Producer" },
  { value: "account_manager", label: "Account Manager" },
  { value: "service_rep", label: "Service Representative" },
  { value: "read_only", label: "Read Only" },
  { value: "restricted", label: "Restricted" },
] };
const USER_FIELDS = [
  { key: "name", label: "Name", type: "text", required: true },
  { key: "email", label: "Email", type: "text", required: true },
  { key: "role", label: "Role", type: "select", options: ROLE_OPTS, required: true },
  { key: "password", label: "Password", type: "text" },
  { key: "active_flag", label: "Active", type: "switch" },
  { key: "licensed_states", label: "Licensed States (comma)", type: "text" },
  { key: "lines_of_authority", label: "Lines of Authority (comma)", type: "text" },
  { key: "license_status", label: "License Status", type: "text" },
  { key: "license_expiration", label: "License Expiration", type: "date" },
];

function Users() {
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [filter, setFilter] = useState({ role: "", status: "", q: "" });
  const [invMap, setInvMap] = useState({});
  const load = async () => {
    try {
      const { data } = await api.get("/auth/users"); setUsers(data.items);
      try { const inv = await api.get("/auth/invitations"); const m = {}; (inv.data.items || []).forEach((i) => { if (!m[i.email] || i.created_at > m[i.email].created_at) m[i.email] = i; }); setInvMap(m); } catch {}
    } catch { toast.error("Could not load users."); }
  };
  useEffect(() => { load(); }, []);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const deactivate = (u) => act(() => api.post(`/auth/users/${u.id}/deactivate`), "User deactivated; sessions ended.");
  const reactivate = (u) => act(() => api.post(`/auth/users/${u.id}/reactivate`), "User reactivated.");
  const forceReset = (u) => act(() => api.post(`/auth/users/${u.id}/force-reset`), "User must reset password next login.");
  const resendInvite = (u) => { const inv = invMap[u.email]; if (!inv) return toast.error("No invitation on file."); act(() => api.post(`/auth/invitations/${inv.id}/resend`), "Invitation re-sent."); };
  const revokeInvite = (u) => { const inv = invMap[u.email]; if (!inv) return toast.error("No invitation on file."); act(() => api.post(`/auth/invitations/${inv.id}/revoke`), "Invitation revoked."); };

  const filtered = users.filter((u) =>
    (!filter.role || u.role === filter.role) &&
    (!filter.status || (filter.status === "active" ? u.active_flag : filter.status === "inactive" ? !u.active_flag : u.invitation_status === "pending")) &&
    (!filter.q || (u.name + u.email).toLowerCase().includes(filter.q.toLowerCase())));

  const InvBadge = ({ s }) => s === "pending" ? <Badge className="bg-amber-100 text-amber-700 border-amber-300">Invited</Badge>
    : s === "accepted" ? <Badge className="bg-seagreen/10 text-seagreen border-seagreen/30">Accepted</Badge>
    : s === "expired" ? <Badge className="bg-slate-100 text-slate-500 border-slate-300">Expired</Badge>
    : s === "revoked" ? <Badge className="bg-seared/10 text-seared border-seared/30">Revoked</Badge> : <span className="text-mutedsea text-xs">—</span>;

  return (
    <div>
      <div className="flex flex-wrap gap-2 justify-between mb-3 items-center">
        <div className="flex flex-wrap gap-2">
          <Input placeholder="Search name/email" data-testid="user-search" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} className="w-48 h-9" />
          <select data-testid="filter-role" value={filter.role} onChange={(e) => setFilter({ ...filter, role: e.target.value })} className="h-9 border border-border rounded-sm px-2 text-sm">
            <option value="">All roles</option>{ROLE_OPTS.static.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <select data-testid="filter-status" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className="h-9 border border-border rounded-sm px-2 text-sm">
            <option value="">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option><option value="pending">Pending invite</option>
          </select>
        </div>
        <div className="flex gap-2">
          <Button data-testid="reassign-book-btn" variant="outline" onClick={() => setDialog({ mode: "reassign" })}>Reassign Book</Button>
          <Button data-testid="invite-user" variant="outline" onClick={() => setDialog({ mode: "invite", record: { role: "producer" } })}><Mail className="h-4 w-4 mr-1.5" /> Invite</Button>
          <Button data-testid="create-user" onClick={() => setDialog({ mode: "create", record: { active_flag: true, role: "read_only" } })} className="bg-navy hover:bg-atlantic text-white"><Plus className="h-4 w-4 mr-1.5" /> New User</Button>
        </div>
      </div>
      <div className="bg-white border border-border rounded-sm overflow-x-auto end-scroll">
        <table className="w-full text-sm">
          <thead><tr className="bg-cloud border-b border-border">{["Name", "Role", "Book (C/P/O/T)", "AM/CSR", "Status", "Invite", "Last Login", "Actions"].map((h) => <th key={h} className="text-left px-3 py-2 text-xs uppercase text-navy font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} data-testid={`user-${u.id}`} className="border-b border-border last:border-0 hover:bg-ice">
                <td className="px-3 py-2"><div className="text-slate font-medium">{u.name}</div><div className="text-xs text-mutedsea">{u.email}</div></td>
                <td className="px-3 py-2"><Badge variant="outline" className="bg-ice text-atlantic border-sky/50">{u.role_label}</Badge></td>
                <td className="px-3 py-2 text-xs text-mutedsea whitespace-nowrap">{u.counts ? `${u.counts.clients}/${u.counts.prospects}/${u.counts.opportunities}/${u.counts.tasks}` : "—"}</td>
                <td className="px-3 py-2 text-xs text-mutedsea whitespace-nowrap">{u.counts ? `${u.counts.as_account_manager}/${u.counts.as_csr}` : "—"}</td>
                <td className="px-3 py-2">{u.active_flag ? <Badge variant="outline" className="bg-seagreen/10 text-seagreen border-seagreen/30">Active</Badge> : <Badge variant="outline" className="bg-seared/10 text-seared border-seared/30">Inactive</Badge>}</td>
                <td className="px-3 py-2"><InvBadge s={u.invitation_status} /></td>
                <td className="px-3 py-2 text-xs text-mutedsea whitespace-nowrap">{u.last_login ? new Date(u.last_login).toLocaleDateString() : "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    <button data-testid={`edit-${u.id}`} onClick={() => setDialog({ mode: "edit", record: u })} className="text-xs px-2 py-1 border border-border rounded-sm hover:bg-cloud">Edit</button>
                    <button data-testid={`resetpw-${u.id}`} onClick={() => setDialog({ mode: "resetpw", record: u })} className="text-xs px-2 py-1 border border-border rounded-sm hover:bg-cloud">Reset PW</button>
                    <button data-testid={`force-${u.id}`} onClick={() => forceReset(u)} className="text-xs px-2 py-1 border border-border rounded-sm hover:bg-cloud">Force Reset</button>
                    <button data-testid={`security-${u.id}`} onClick={() => setDialog({ mode: "security", record: u })} className="text-xs px-2 py-1 border border-border rounded-sm hover:bg-cloud">Security</button>
                    {u.invitation_status === "pending" && <>
                      <button data-testid={`resend-${u.id}`} onClick={() => resendInvite(u)} className="text-xs px-2 py-1 border border-atlantic/40 text-atlantic rounded-sm hover:bg-ice">Resend</button>
                      <button data-testid={`revoke-${u.id}`} onClick={() => revokeInvite(u)} className="text-xs px-2 py-1 border border-seared/40 text-seared rounded-sm hover:bg-seared/5">Revoke</button>
                    </>}
                    {u.active_flag
                      ? <button data-testid={`deactivate-${u.id}`} onClick={() => deactivate(u)} className="text-xs px-2 py-1 border border-seared/40 text-seared rounded-sm hover:bg-seared/5">Deactivate</button>
                      : <button data-testid={`reactivate-${u.id}`} onClick={() => reactivate(u)} className="text-xs px-2 py-1 border border-seagreen/40 text-seagreen rounded-sm hover:bg-seagreen/5">Reactivate</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dialog?.mode === "invite" && <InviteDialog onClose={() => { setDialog(null); load(); }} />}
      {dialog?.mode === "reassign" && <ReassignDialog users={users} onClose={() => { setDialog(null); load(); }} />}
      {dialog?.mode === "resetpw" && <ResetPwDialog user={dialog.record} onClose={() => { setDialog(null); load(); }} />}
      {dialog?.mode === "security" && <SecurityDialog user={dialog.record} onClose={() => setDialog(null)} />}
      {(dialog?.mode === "create" || dialog?.mode === "edit") && (
        <RecordDialog open onOpenChange={() => setDialog(null)} fields={USER_FIELDS}
          title={dialog.mode === "create" ? "New User" : "Edit User"} initial={dialog.record}
          endpoint={dialog.mode === "create" ? "/auth/users" : `/auth/users/${dialog.record.id}`}
          method={dialog.mode === "create" ? "post" : "put"} onSaved={() => { setDialog(null); load(); }} />
      )}
    </div>
  );
}

function Modal({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid={testid}>
      <div className="bg-white rounded-sm border border-border w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center px-5 py-3 border-b border-border"><h3 className="font-serif text-lg text-navy">{title}</h3><button onClick={onClose}><X className="h-4 w-4 text-mutedsea" /></button></div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function InviteDialog({ onClose }) {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", role: "producer" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/invitations", f);
      toast.success(data.message);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Modal title="Invite User" onClose={onClose} testid="invite-dialog">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div><label className="text-sm">First name</label><Input data-testid="inv-first" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} /></div>
        <div><label className="text-sm">Last name</label><Input data-testid="inv-last" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} /></div>
      </div>
      <label className="text-sm">Email</label><Input data-testid="inv-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mb-3" />
      <label className="text-sm">Role</label>
      <select data-testid="inv-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="w-full h-9 border border-border rounded-sm px-2 text-sm mb-4">
        {ROLE_OPTS.static.filter((r) => r.value !== "super_admin").map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
      </select>
      <p className="text-xs text-mutedsea mb-3">A one-time expiring link is emailed via Proton. If Proton is unavailable, use “Reset PW” to issue a temporary password instead.</p>
      <Button data-testid="inv-submit" disabled={busy} onClick={submit} className="w-full bg-navy hover:bg-atlantic text-white">{busy ? "Sending…" : "Send Invitation"}</Button>
    </Modal>
  );
}

function ReassignDialog({ users, onClose }) {
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [roles, setRoles] = useState({ producer: true, account_manager: false, csr: false });
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const selRoles = () => Object.keys(roles).filter((r) => roles[r]);
  const doPreview = async () => {
    if (!from || !to) return toast.error("Select both a FROM and TO user.");
    setBusy(true);
    try { const { data } = await api.post("/admin/reassign-preview", { from_user_id: from, to_user_id: to, roles: selRoles() }); setPreview(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const submit = async () => {
    if (!reason.trim()) return toast.error("A reason is required.");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reassign-book", { from_user_id: from, to_user_id: to, roles: selRoles(), reason });
      toast.success(`Reassigned: ${data.moved.parties} clients/prospects, ${data.moved.children} records, ${data.moved.leads} leads.`);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Modal title="Reassignment Center — Reassign Book" onClose={onClose} testid="reassign-dialog">
      <label className="text-sm">From user</label>
      <select data-testid="reassign-from" value={from} onChange={(e) => { setFrom(e.target.value); setPreview(null); }} className="w-full h-9 border border-border rounded-sm px-2 text-sm mb-3"><option value="">Select…</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}</select>
      <label className="text-sm">To user</label>
      <select data-testid="reassign-to" value={to} onChange={(e) => { setTo(e.target.value); setPreview(null); }} className="w-full h-9 border border-border rounded-sm px-2 text-sm mb-3"><option value="">Select…</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}</select>
      <div className="flex gap-4 mb-3 text-sm">
        {["producer", "account_manager", "csr"].map((r) => <label key={r} className="flex items-center gap-1"><input type="checkbox" data-testid={`reassign-${r}`} checked={roles[r]} onChange={(e) => { setRoles({ ...roles, [r]: e.target.checked }); setPreview(null); }} /> {r.replace("_", " ")}</label>)}
      </div>
      <label className="text-sm">Reason (required)</label>
      <Input data-testid="reassign-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Producer departure — book transfer" className="mb-3" />
      {!preview ? (
        <Button data-testid="reassign-preview-btn" disabled={busy} onClick={doPreview} variant="outline" className="w-full border-atlantic text-atlantic">{busy ? "Calculating…" : "Preview Impact"}</Button>
      ) : (
        <>
          <div className="bg-cloud border border-border rounded-sm p-3 mb-3 text-sm" data-testid="reassign-impact">
            <div className="font-medium text-navy mb-1">Impact preview — {preview.total_records} record(s) will move</div>
            {Object.entries(preview.impact).map(([field, counts]) => (
              <div key={field} className="text-xs text-mutedsea">{field.replace("_id", "").replace("_", " ")}: {Object.entries(counts).map(([k, v]) => `${v} ${k}`).join(", ")}</div>
            ))}
          </div>
          <p className="text-xs text-mutedsea mb-3">Moves live servicing responsibility only. Historical production attribution, commission ledger, chronology and audit are preserved.</p>
          <Button data-testid="reassign-submit" disabled={busy} onClick={submit} className="w-full bg-navy hover:bg-atlantic text-white">{busy ? "Reassigning…" : "Confirm Reassignment"}</Button>
        </>
      )}
    </Modal>
  );
}

function ResetPwDialog({ user, onClose }) {
  const [pw, setPw] = useState(""); const [result, setResult] = useState("");
  const submit = async () => {
    try { const { data } = await api.post(`/auth/users/${user.id}/reset-password`, pw ? { password: pw } : {}); setResult(data.temp_password); toast.success("Temporary password set (force change on next login)."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal title={`Reset Password — ${user.name}`} onClose={onClose} testid="resetpw-dialog">
      {!result ? (<>
        <p className="text-sm text-mutedsea mb-3">Leave blank to auto-generate a secure temporary password. The user must change it on next login.</p>
        <Input data-testid="resetpw-input" placeholder="Temporary password (optional)" value={pw} onChange={(e) => setPw(e.target.value)} className="mb-4" />
        <Button data-testid="resetpw-submit" onClick={submit} className="w-full bg-navy text-white">Set Temporary Password</Button>
      </>) : (<>
        <p className="text-sm text-slate mb-2">Relay this temporary password securely. It cannot be retrieved again:</p>
        <div className="bg-cloud border border-border rounded-sm p-3 font-mono text-sm break-all" data-testid="resetpw-result">{result}</div>
        <Button onClick={onClose} className="w-full mt-4 bg-navy text-white">Done</Button>
      </>)}
    </Modal>
  );
}

function SecurityDialog({ user, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { (async () => { try { const { data } = await api.get(`/auth/users/${user.id}/security`); setD(data); } catch { toast.error("Could not load security activity."); } })(); }, [user.id]);
  return (
    <Modal title={`Security Activity — ${user.name}`} onClose={onClose} testid="security-dialog">
      {!d ? <p className="text-mutedsea">Loading…</p> : (
        <div className="space-y-2 text-sm">
          <Row2 k="Last login" v={d.last_login ? new Date(d.last_login).toLocaleString() : "Never"} />
          <Row2 k="Active" v={d.active_flag ? "Yes" : "No"} />
          <Row2 k="Force reset pending" v={d.must_change_password ? "Yes" : "No"} />
          <Row2 k="Invitation" v={d.invitation_status} />
          <Row2 k="Failed logins" v={String(d.failed_login_count)} />
          <Row2 k="Session version" v={String(d.token_version)} />
          <div className="pt-2 border-t border-border mt-2"><div className="text-xs uppercase text-navy font-semibold mb-1">Events</div>
            {(d.events || []).length === 0 ? <p className="text-mutedsea text-xs">No security events recorded.</p> :
              d.events.map((e) => <div key={e.id} className="flex justify-between text-xs py-1 border-b border-border/50"><span className="text-slate">{e.event} <span className="text-mutedsea">by {e.actor}</span></span><span className="text-mutedsea">{new Date(e.at).toLocaleString()}</span></div>)}
          </div>
        </div>
      )}
    </Modal>
  );
}

function UnassignedRecords() {
  const [items, setItems] = useState([]); const [users, setUsers] = useState([]);
  const [sel, setSel] = useState({}); const [assign, setAssign] = useState({ primary_producer_id: "", account_manager_id: "", csr_id: "" });
  const load = async () => {
    try { const [a, b] = await Promise.all([api.get("/admin/unassigned"), api.get("/auth/users")]); setItems(a.data.items); setUsers(b.data.items); }
    catch { toast.error("Could not load unassigned records."); }
  };
  useEffect(() => { load(); }, []);
  const chosen = Object.keys(sel).filter((k) => sel[k]);
  const doAssign = async () => {
    const body = {};
    ["primary_producer_id", "account_manager_id", "csr_id"].forEach((f) => { if (assign[f]) body[f] = assign[f]; });
    if (!Object.keys(body).length) return toast.error("Choose at least one assignment.");
    if (!chosen.length) return toast.error("Select at least one record.");
    try {
      const byRes = {}; items.filter((i) => sel[i.id]).forEach((i) => { (byRes[i.resource] = byRes[i.resource] || []).push(i.id); });
      for (const [resource, ids] of Object.entries(byRes)) await api.post("/admin/assign", { resource, record_ids: ids, ...body });
      toast.success(`Assigned ${chosen.length} record(s).`); setSel({}); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doAssignHouse = async () => {
    if (!chosen.length) return toast.error("Select at least one record.");
    try {
      const byRes = {}; items.filter((i) => sel[i.id]).forEach((i) => { (byRes[i.resource] = byRes[i.resource] || []).push(i.id); });
      for (const [resource, ids] of Object.entries(byRes)) await api.post("/admin/assign", { resource, record_ids: ids, house: true });
      toast.success(`Assigned ${chosen.length} record(s) to House.`); setSel({}); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <p className="text-sm text-mutedsea mb-3">{items.length} record(s) with no resolvable producer/AM/CSR. Assign deliberately — nothing is auto-assigned.</p>
      <div className="flex flex-wrap gap-2 items-end mb-3 bg-cloud border border-border rounded-sm p-3">
        {[["primary_producer_id", "Producer"], ["account_manager_id", "Account Manager"], ["csr_id", "CSR"]].map(([k, lbl]) => (
          <div key={k}><label className="text-xs text-navy block">{lbl}</label>
            <select data-testid={`assign-${k}`} value={assign[k]} onChange={(e) => setAssign({ ...assign, [k]: e.target.value })} className="h-9 border border-border rounded-sm px-2 text-sm w-40"><option value="">—</option>{users.filter((u) => u.active_flag).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
          </div>))}
        <Button data-testid="assign-submit" onClick={doAssign} className="bg-navy text-white h-9">Assign Selected ({chosen.length})</Button>
        <Button data-testid="assign-house" onClick={doAssignHouse} variant="outline" className="border-atlantic text-atlantic h-9">Assign to House ({chosen.length})</Button>
      </div>
      <div className="bg-white border border-border rounded-sm overflow-x-auto end-scroll">
        <table className="w-full text-sm"><thead><tr className="bg-cloud border-b border-border">{["", "Name", "Type", "Legacy Producer", "Related", "Reason"].map((h) => <th key={h} className="text-left px-3 py-2 text-xs uppercase text-navy font-semibold">{h}</th>)}</tr></thead>
          <tbody>{items.map((i) => (
            <tr key={i.id} data-testid={`unassigned-${i.id}`} className="border-b border-border last:border-0 hover:bg-ice">
              <td className="px-3 py-2"><input type="checkbox" data-testid={`select-${i.id}`} checked={!!sel[i.id]} onChange={(e) => setSel({ ...sel, [i.id]: e.target.checked })} /></td>
              <td className="px-3 py-2 text-slate">{i.name}</td><td className="px-3 py-2 text-xs">{i.type}</td>
              <td className="px-3 py-2 text-xs text-mutedsea">{i.legacy_producer || "—"}</td>
              <td className="px-3 py-2 text-xs text-mutedsea">{i.related_policy || i.related_opportunity || "—"}</td>
              <td className="px-3 py-2 text-xs text-mutedsea">{i.reason}</td>
            </tr>))}</tbody>
        </table>
      </div>
    </div>
  );
}

function ConfigEditor() {
  const [config, setConfig] = useState({});
  const load = async () => { const { data } = await api.get("/config"); setConfig(data.items); };
  useEffect(() => { load(); }, []);

  const saveList = async (key, value) => {
    try { await api.put(`/config/${key}`, { value }); toast.success("Configuration updated."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const listKeys = ["service_case_types", "case_priorities", "document_categories", "activity_types", "service_tiers", "loss_reasons", "renewal_outcomes", "life_event_types"];

  return (
    <div className="space-y-5">
      <div className="bg-white border border-border rounded-sm p-4">
        <h3 className="font-serif text-lg text-navy mb-2">Renewal Timing (days before expiration)</h3>
        <div className="flex gap-4 items-end">
          <div><label className="text-xs text-mutedsea block mb-1">Personal</label><Input type="number" data-testid="cfg-personal-days" defaultValue={config.renewal_timing?.personal_days} className="w-28 bg-white" id="pd" /></div>
          <div><label className="text-xs text-mutedsea block mb-1">Commercial</label><Input type="number" data-testid="cfg-commercial-days" defaultValue={config.renewal_timing?.commercial_days} className="w-28 bg-white" id="cd" /></div>
          <Button data-testid="save-renewal-timing" onClick={() => saveList("renewal_timing", { personal_days: Number(document.getElementById("pd").value), commercial_days: Number(document.getElementById("cd").value) })} className="bg-navy hover:bg-atlantic text-white">Save</Button>
        </div>
      </div>
      {listKeys.map((k) => <ListEditor key={k} keyName={k} values={config[k] || []} onSave={saveList} />)}
    </div>
  );
}

function ListEditor({ keyName, values, onSave }) {
  const [items, setItems] = useState(values);
  const [nv, setNv] = useState("");
  useEffect(() => { setItems(values); }, [values]);
  return (
    <div className="bg-white border border-border rounded-sm p-4">
      <h3 className="font-serif text-lg text-navy mb-2 capitalize">{keyName.replace(/_/g, " ")}</h3>
      <div className="flex flex-wrap gap-2 mb-2">
        {items.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1 bg-ice text-atlantic border border-sky/50 rounded-sm px-2 py-0.5 text-xs">
            {it}<button onClick={() => setItems(items.filter((_, j) => j !== i))} className="hover:text-seared"><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={nv} onChange={(e) => setNv(e.target.value)} placeholder="Add value…" className="h-8 text-sm bg-white max-w-xs" data-testid={`cfg-add-${keyName}`} />
        <Button size="sm" variant="outline" onClick={() => { if (nv) { setItems([...items, nv]); setNv(""); } }}>Add</Button>
        <Button size="sm" onClick={() => onSave(keyName, items)} className="bg-navy hover:bg-atlantic text-white" data-testid={`cfg-save-${keyName}`}>Save</Button>
      </div>
    </div>
  );
}

function Integrations() {
  const { can } = useAuth();
  const [status, setStatus] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, c] = await Promise.all([api.get("/dropbox/status"), api.get("/dropbox/config-status")]);
      setStatus(s.data); setCfg(c.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("dropbox");
    if (p === "connected") toast.success("Dropbox connected successfully.");
    else if (p) toast.error(`Dropbox connection issue: ${p}`);
    if (p) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const connect = async () => {
    setBusy(true);
    try { const { data } = await api.get("/dropbox/authorize"); window.location.href = data.url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post("/dropbox/test"); toast.success(`Verified: ${data.account_name} (${data.account_email}). Root OK.`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const reconcile = async () => {
    setBusy(true);
    try { const { data } = await api.post("/dropbox/reconcile"); toast.success(`Reconciled: ${data.files_checked} checked, ${data.new} new, ${data.moved} moved, ${data.missing} missing.`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Dropbox? Document metadata and audit history are kept; Dropbox files are not deleted.")) return;
    setBusy(true);
    try { await api.post("/dropbox/disconnect"); toast.success("Dropbox disconnected."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const connected = status?.connected;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-white border border-border rounded-sm p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-serif text-xl text-navy flex items-center gap-2"><Cloud className="h-5 w-5 text-atlantic" /> Dropbox</h3>
          <Badge variant="outline" className={connected ? "bg-seagreen/10 text-seagreen border-seagreen/30" : (cfg && !cfg.configured ? "bg-seared/10 text-seared border-seared/30" : "bg-seaamber/10 text-seaamber border-seaamber/30")} data-testid="dropbox-status">
            {connected ? "Connected / Healthy" : (cfg && !cfg.configured ? "Configuration Required" : "Not Connected")}
          </Badge>
        </div>
        <p className="text-sm text-mutedsea mb-3">Authoritative document repository. Access model: <b>App Folder</b>. Least-privilege scopes only.</p>

        {connected ? (
          <div className="text-sm space-y-1 mb-4">
            <Row2 k="Account" v={status.account_name} /><Row2 k="Email" v={status.account_email} />
            <Row2 k="Account ID" v={status.account_id} /><Row2 k="Connected" v={status.connected_at?.slice(0, 10)} />
            <Row2 k="Last Verified" v={status.last_verified ? new Date(status.last_verified).toLocaleString() : "—"} />
            <Row2 k="Root" v={status.root} /><Row2 k="Scopes" v={(status.scopes || []).join(", ")} />
          </div>
        ) : (
          <div className="text-sm mb-4 space-y-1">
            <Row2 k="App Key" v={cfg?.app_key_set ? "Set ✓" : "Missing"} />
            <Row2 k="App Secret" v={cfg?.app_secret_set ? "Set ✓" : "Missing"} />
            <Row2 k="Redirect URI" v={cfg?.redirect_uri || "Missing"} />
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {can.admin && cfg?.configured && !connected && <Button onClick={connect} disabled={busy} data-testid="connect-dropbox" className="bg-navy hover:bg-atlantic text-white">{busy ? "…" : "Connect Dropbox"}</Button>}
          {can.admin && connected && <>
            <Button onClick={test} disabled={busy} variant="outline" data-testid="test-dropbox" className="border-atlantic text-atlantic">Test Connection</Button>
            <Button onClick={reconcile} disabled={busy} variant="outline" data-testid="reconcile-dropbox" className="border-atlantic text-atlantic">Reconcile</Button>
            <Button onClick={connect} disabled={busy} variant="outline" className="border-atlantic text-atlantic">Reconnect</Button>
            <Button onClick={disconnect} disabled={busy} variant="outline" data-testid="disconnect-dropbox" className="border-seared text-seared">Disconnect</Button>
          </>}
          <Button onClick={() => setShowSetup(!showSetup)} variant="ghost" data-testid="dropbox-setup" className="text-mutedsea">Setup Instructions</Button>
        </div>

        {showSetup && (
          <div className="mt-4 border-t border-border pt-3 text-sm text-slate space-y-1.5" data-testid="dropbox-setup-panel">
            <p className="font-medium text-navy">Dropbox Developer Console setup</p>
            <ol className="list-decimal ml-5 space-y-1 text-mutedsea">
              <li>Open the Dropbox App Console → Create app.</li>
              <li>Choose <b>Scoped access</b> → <b>App folder</b>.</li>
              <li>App name: <b>Endurance CRM</b>.</li>
              <li>Under OAuth 2 → Redirect URIs, add exactly:<br/><code className="text-atlantic break-all">{cfg?.redirect_uri}</code></li>
              <li>Under Permissions, enable: <code className="text-atlantic">{(cfg?.scopes || []).join(", ")}</code>, then Submit.</li>
              <li>App Key and App Secret are stored in server secrets ({cfg?.app_key_set ? "already set ✓" : "not set"}).</li>
              <li>Click <b>Connect Dropbox</b> and authorize at Dropbox.</li>
            </ol>
          </div>
        )}
      </div>

      <EmailIntegration can={can} />
      <ServiceStatusCards />
    </div>
  );
}

function ServiceStatusCards() {
  const [intel, setIntel] = useState(null);
  const [mkt, setMkt] = useState(null);
  useEffect(() => {
    api.get("/intelligence/config").then(({ data }) => setIntel(data)).catch(() => setIntel({}));
    api.get("/marketing/config").then(({ data }) => setMkt(data)).catch(() => setMkt({}));
  }, []);
  const cards = [
    { name: "Apollo (Prospect Data)", icon: Cloud, status: intel ? (intel.allow_email_enrichment != null ? "Configured" : "Config Pending") : "…",
      ok: !!intel, rows: intel ? [["Org enrich / day", intel.max_org_enrich_per_day], ["People enrich / day", intel.max_people_enrich_per_day], ["Email enrichment", intel.allow_email_enrichment ? "Allowed" : "Off"]] : [] },
    { name: "LLM (Emergent Universal Key)", icon: ShieldCheck, status: "Configured", ok: true,
      rows: [["Provider", "Emergent LLM"], ["Used by", "Intelligence, BOI, drafting"]] },
    { name: "Website Intake", icon: Mail, status: mkt && mkt.products ? "Active" : "…", ok: !!(mkt && mkt.products),
      rows: mkt ? [["Products", (mkt.products || []).length], ["Geographies", (mkt.geographies || []).length], ["Channels", (mkt.channels || []).length]] : [] },
    { name: "IVANS / Carrier Download Feed", icon: Cloud, status: "No live feed connected", ok: false,
      rows: [["Feed", "Not connected"], ["Import path", "Manual / Policy Document Import"]] },
  ];
  return (<>
    {cards.map((c) => (
      <div key={c.name} className="bg-white border border-border rounded-sm p-5" data-testid={`integration-card-${c.name.split(" ")[0].toLowerCase()}`}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-serif text-lg text-navy flex items-center gap-2"><c.icon className="h-5 w-5 text-atlantic" /> {c.name}</h3>
          <Badge variant="outline" className={c.ok ? "bg-seagreen/10 text-seagreen border-seagreen/30" : "bg-seaamber/10 text-seaamber border-seaamber/30"}>{c.status}</Badge>
        </div>
        <div className="text-sm space-y-1">{c.rows.map(([k, v]) => <Row2 key={k} k={k} v={String(v ?? "—")} />)}</div>
      </div>
    ))}
  </>);
}

function EmailIntegration({ can }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const load = async () => { try { const { data } = await api.get("/email/status"); setSt(data); } catch {} };
  useEffect(() => { load(); }, []);
  const testConn = async () => { setBusy(true); try { const { data } = await api.post("/email/test"); toast.success(data.message); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const sendTest = async () => { if (!testTo) { toast.error("Enter a recipient."); return; } setBusy(true); try { await api.post("/email/send-test", { to: testTo }); toast.success("Test email queued."); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const toggleDisable = async () => { setBusy(true); try { await api.post("/email/config", { sending_disabled: !st?.sending_disabled }); toast.success(st?.sending_disabled ? "Sending enabled." : "Sending disabled."); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const configured = st?.configured;
  return (
    <div className="bg-white border border-border rounded-sm p-5" data-testid="email-integration-card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-serif text-xl text-navy flex items-center gap-2"><Mail className="h-5 w-5 text-atlantic" /> Email (Proton SMTP)</h3>
        <Badge variant="outline" className={configured ? "bg-seagreen/10 text-seagreen border-seagreen/30" : "bg-seaamber/10 text-seaamber border-seaamber/30"} data-testid="email-integration-status">{st?.label || "…"}</Badge>
      </div>
      <p className="text-sm text-mutedsea mb-3">Provider-independent email service with a durable send queue. Credentials are server-side secrets only.</p>
      <div className="text-sm mb-4 space-y-1">
        <Row2 k="Provider" v={st?.provider} />
        <Row2 k="From Address" v={st?.from_address} />
        <Row2 k="Token" v={st?.token_set ? "Set ✓" : "Configuration Pending"} />
        <Row2 k="Queue Depth" v={String(st?.queue_depth ?? "—")} />
        <Row2 k="Failed" v={String(st?.failed_count ?? "—")} />
        <Row2 k="Last Success" v={st?.last_success ? new Date(st.last_success).toLocaleString() : "—"} />
        <Row2 k="Last Failure" v={st?.last_failure ? new Date(st.last_failure).toLocaleString() : "—"} />
      </div>
      {can.admin && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button onClick={testConn} disabled={busy || !configured} variant="outline" data-testid="email-test-connection" className="border-atlantic text-atlantic">Test Connection</Button>
            <Button onClick={toggleDisable} disabled={busy} variant="outline" data-testid="email-toggle-sending" className="border-seared text-seared">{st?.sending_disabled ? "Enable Sending" : "Disable Sending"}</Button>
          </div>
          <div className="flex gap-2">
            <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="email-test-to" placeholder="you@example.com" className="bg-white text-sm h-9" />
            <Button onClick={sendTest} disabled={busy || !configured} data-testid="email-send-test" className="bg-navy hover:bg-atlantic text-white">Send Test Email</Button>
          </div>
          {!configured && <p className="text-xs text-seaamber">Configuration Pending — set PROTON_SMTP_TOKEN in server secrets to enable sending.</p>}
        </div>
      )}
    </div>
  );
}
const Row2 = ({ k, v }) => (<div className="flex justify-between gap-4"><span className="text-mutedsea">{k}</span><span className="text-slate text-right break-all">{v || "—"}</span></div>);

function RecordsIntegrity() {
  const [verify, setVerify] = useState(null);
  const [backups, setBackups] = useState({ items: [], status: {} });
  const [busy, setBusy] = useState(false);

  const loadBackups = async () => {
    try { const { data } = await api.get("/chronology/backups"); setBackups(data); } catch {}
  };
  useEffect(() => { loadBackups(); }, []);

  const runVerify = async () => {
    setBusy(true);
    try { const { data } = await api.get("/chronology/ledger/verify"); setVerify(data);
      toast[data.intact ? "success" : "error"](data.intact ? `Chain intact — ${data.total} sealed records.` : `Integrity breaks: ${data.breaks.length}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const runBackup = async () => {
    setBusy(true);
    try { const { data } = await api.post("/chronology/backup/run"); toast.success(`Backup filed to Dropbox: ${data.records} records.`); loadBackups(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const exportAll = async (fmt) => {
    try {
      const res = await api.get("/chronology/export", { params: { format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `agency_chronology_all_${new Date().toISOString().replace(/[:.]/g, "").slice(0, 15)}.${fmt}`; a.click(); URL.revokeObjectURL(url);
      toast.success(`Agency chronology exported (${fmt.toUpperCase()}).`);
    } catch (e) { toast.error("Export failed."); }
  };

  return (
    <div className="space-y-4" data-testid="admin-records">
      <div className="bg-white border border-border rounded-sm p-4">
        <h3 className="font-serif text-lg text-navy flex items-center gap-2 mb-1"><ShieldCheck className="h-4 w-4 text-atlantic" /> Chronology Integrity (tamper-evident ledger)</h3>
        <p className="text-sm text-mutedsea mb-3">Every chronology entry and filed document is digitally time-stamped (locked UTC) and chained with SHA-256. Verify the whole chain end-to-end.</p>
        <Button data-testid="ledger-verify-btn" size="sm" onClick={runVerify} disabled={busy} className="bg-atlantic hover:bg-ocean text-white">Verify integrity now</Button>
        {verify && (
          <div className="mt-3 text-sm" data-testid="ledger-verify-result">
            <Badge className={verify.intact ? "bg-seagreen/15 text-seagreen border-seagreen/30" : "bg-seared/15 text-seared border-seared/30"} variant="outline">
              {verify.intact ? "Chain intact" : `${verify.breaks.length} break(s)`}
            </Badge>
            <span className="text-mutedsea ml-2">{verify.total} sealed records · last sealed {verify.last_sealed_at || "—"}</span>
            <div className="text-xs text-mutedsea mt-1 break-all">Head hash: {verify.head_hash}</div>
          </div>
        )}
      </div>

      <div className="bg-white border border-border rounded-sm p-4">
        <h3 className="font-serif text-lg text-navy flex items-center gap-2 mb-1"><Cloud className="h-4 w-4 text-atlantic" /> Backups & Export</h3>
        <p className="text-sm text-mutedsea mb-3">A full backup (chronology + ledger, JSON &amp; CSV) is filed to Dropbox automatically every day at 07:00 UTC. You can also back up or export on demand.</p>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="backup-now-btn" size="sm" onClick={runBackup} disabled={busy} className="bg-atlantic hover:bg-ocean text-white">Back up to Dropbox now</Button>
          <Button data-testid="export-all-csv" size="sm" variant="outline" onClick={() => exportAll("csv")} className="border-atlantic text-atlantic"><Download className="h-3.5 w-3.5 mr-1" />Export all (CSV)</Button>
          <Button data-testid="export-all-json" size="sm" variant="outline" onClick={() => exportAll("json")} className="border-atlantic text-atlantic"><Download className="h-3.5 w-3.5 mr-1" />Export all (JSON)</Button>
        </div>
        {backups.status?.last_backup_at && (
          <p className="text-xs text-mutedsea mt-3">Last backup: <strong>{backups.status.last_status}</strong> · {backups.status.last_backup_at} · {backups.status.last_record_count} records → {backups.status.last_folder}</p>
        )}
        {backups.items?.length > 0 && (
          <div className="mt-3 space-y-1" data-testid="backup-history">
            {backups.items.slice(0, 8).map((b) => (
              <div key={b.id} className="text-xs text-mutedsea flex items-center gap-2">
                <Badge variant="outline" className={b.status === "Completed" ? "bg-seagreen/10 text-seagreen border-seagreen/30" : b.status === "Failed" ? "bg-seared/10 text-seared border-seared/30" : "bg-cloud text-slate border-border"}>{b.status}</Badge>
                {b.started_at} · {b.record_count} records · {b.triggered_by}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Administration() {
  const { can } = useAuth();
  if (!can.admin) return (
    <div className="p-6 animate-fade-in">
      <PageHeader title="Administration" />
      <div className="bg-white border border-border rounded-sm p-10 text-center" data-testid="admin-access-denied">
        <Lock className="h-8 w-8 mx-auto text-mutedsea mb-2" />
        <p className="text-slate font-medium">Administrator access required</p>
        <p className="text-sm text-mutedsea mt-1">You do not have permission to view this area.</p>
      </div>
    </div>
  );
  return (
    <div className="p-6 animate-fade-in">
      <PageHeader title="Administration" subtitle="Users, permissions, configurable lists and integrations — all editable without code deployment." />
      <Tabs defaultValue="users">
        <TabsList className="bg-white border border-border">
          <TabsTrigger value="users" data-testid="admin-tab-users" className="data-[state=active]:bg-navy data-[state=active]:text-white">Users & Roles</TabsTrigger>
          <TabsTrigger value="unassigned" data-testid="admin-tab-unassigned" className="data-[state=active]:bg-navy data-[state=active]:text-white">Unassigned Records</TabsTrigger>
          <TabsTrigger value="config" data-testid="admin-tab-config" className="data-[state=active]:bg-navy data-[state=active]:text-white">Configuration</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="admin-tab-integrations" className="data-[state=active]:bg-navy data-[state=active]:text-white">Integrations</TabsTrigger>
          <TabsTrigger value="records" data-testid="admin-tab-records" className="data-[state=active]:bg-navy data-[state=active]:text-white">Records &amp; Backups</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-4"><Users /></TabsContent>
        <TabsContent value="unassigned" className="mt-4"><UnassignedRecords /></TabsContent>
        <TabsContent value="config" className="mt-4"><ConfigEditor /></TabsContent>
        <TabsContent value="integrations" className="mt-4"><Integrations /></TabsContent>
        <TabsContent value="records" className="mt-4"><RecordsIntegrity /></TabsContent>
      </Tabs>
    </div>
  );
}
